import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface StockCountLine {
  id: string;
  line_number: number;
  item_id: string;
  item_code?: string | null;
  item_description?: string | null;
  heat_number?: string;
  dimension?: string;
  uom?: string | null;
  system_quantity: number;
  counted_quantity: number | null;
  variance_quantity: number | null;
  unit_cost?: number;
  notes?: string | null;
  adjustment_item_id?: string | null;
}

export interface StockCountSession {
  id: string;
  company_id: string;
  count_number: string;
  warehouse_id: string;
  count_date: string;
  status: string;
  blind_count: boolean;
  department_id?: string | null;
  notes?: string | null;
  stock_adjustment_id?: string | null;
  warehouse?: { id: string; warehouse_name: string; warehouse_code: string };
  lines?: StockCountLine[];
}

class StockCountService {
  async list(params?: Record<string, string>): Promise<StockCountSession[]> {
    const res = await api.getClient().get('/inventory/stock-counts', { params });
    return unwrap<StockCountSession[]>(res);
  }

  async getById(id: string): Promise<StockCountSession> {
    const res = await api.getClient().get(`/inventory/stock-counts/${id}`);
    return unwrap<StockCountSession>(res);
  }

  async create(body: Record<string, unknown>): Promise<StockCountSession> {
    const res = await api.getClient().post('/inventory/stock-counts', body);
    return unwrap<StockCountSession>(res);
  }

  async updateHeader(id: string, body: Record<string, unknown>): Promise<StockCountSession> {
    const res = await api.getClient().put(`/inventory/stock-counts/${id}`, body);
    return unwrap<StockCountSession>(res);
  }

  async updateLines(
    id: string,
    lines: { id: string; counted_quantity: number | null; notes?: string | null }[]
  ): Promise<StockCountSession> {
    const res = await api.getClient().put(`/inventory/stock-counts/${id}/lines`, { lines });
    return unwrap<StockCountSession>(res);
  }

  async snapshot(id: string, include_zero = false): Promise<StockCountSession> {
    const res = await api.getClient().post(`/inventory/stock-counts/${id}/snapshot`, {
      include_zero,
    });
    return unwrap<StockCountSession>(res);
  }

  async submit(id: string): Promise<StockCountSession> {
    const res = await api.getClient().post(`/inventory/stock-counts/${id}/submit`);
    return unwrap<StockCountSession>(res);
  }

  async approve(id: string): Promise<StockCountSession> {
    const res = await api.getClient().post(`/inventory/stock-counts/${id}/approve`);
    return unwrap<StockCountSession>(res);
  }

  async post(id: string): Promise<StockCountSession> {
    const res = await api.getClient().post(`/inventory/stock-counts/${id}/post`);
    return unwrap<StockCountSession>(res);
  }

  async cancel(id: string): Promise<StockCountSession> {
    const res = await api.getClient().post(`/inventory/stock-counts/${id}/cancel`);
    return unwrap<StockCountSession>(res);
  }
}

export default new StockCountService();
